<template>
  <section
    class="nav-drawer-contact mt-auto px-5 pt-5 pb-5"
  >
    <p
      class="text-[10px] font-body uppercase tracking-[0.22em] text-red-500 dark:text-red-400"
    >
      {{ t("nav.drawer.contactEyebrow") }}
    </p>

    <a
      :href="mapsHref"
      target="_blank"
      rel="noopener noreferrer"
      class="group mt-3 flex items-start gap-3 py-1.5 text-slate-700 transition-colors hover:text-red-500 dark:text-slate-200 dark:hover:text-red-400"
      :aria-label="t('nav.drawer.mapsAria')"
      @click="$emit('close')"
    >
      <Icon
        name="mdi-map-marker-outline"
        class="size-[18px] shrink-0 text-red-500 mt-0.5"
        aria-hidden="true"
      />
      <span class="text-sm leading-snug">{{ ADDRESS }}</span>
      <Icon
        name="lucide:arrow-up-right"
        class="size-3.5 ms-auto mt-1 text-slate-400 transition-all group-hover:text-red-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </a>

    <a
      :href="`mailto:${email}`"
      class="group flex items-center gap-3 py-1.5 text-slate-700 transition-colors hover:text-red-500 dark:text-slate-200 dark:hover:text-red-400"
      @click="$emit('close')"
    >
      <Icon
        name="mdi-email-outline"
        class="size-[18px] shrink-0 text-red-500"
        aria-hidden="true"
      />
      <span class="text-sm break-all" dir="ltr">{{ email }}</span>
    </a>

    <a
      v-if="phone"
      :href="`tel:${phone}`"
      class="group flex items-center gap-3 py-1.5 text-slate-700 transition-colors hover:text-red-500 dark:text-slate-200 dark:hover:text-red-400"
      @click="$emit('close')"
    >
      <Icon
        name="ic:baseline-phone"
        class="size-[18px] shrink-0 text-red-500"
        aria-hidden="true"
      />
      <span class="text-sm font-medium tabular-nums" dir="ltr">{{ phone }}</span>
    </a>

    <ul class="mt-4 flex items-center justify-between gap-2 px-4">
      <li v-for="link in socialLinks" :key="link.title">
        <a
          :href="link.href"
          :aria-label="link.title"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex size-9 items-center justify-center rounded-full text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          :class="SOCIAL_BG[link.title] ?? 'bg-slate-500'"
          @click="$emit('close')"
        >
          <Icon :name="link.icon" class="size-[18px]" aria-hidden="true" />
        </a>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
defineEmits<{ close: [] }>();

const { t } = useI18n();
const config = useRuntimeConfig();
const SOCIAL_LINKS = useSocialLinks();

// Mirrors the address shown in the desktop tagline (components/navbar/Tagline.vue).
const ADDRESS = "Batumi st. 16, Chakvi, Georgia";

// Reception coordinates — same point used by location-routes.json and the
// Google Maps deep link in components/NativeMapsLinks.vue.
const RESORT_COORDS = "41.720341,41.728457";
const mapsHref = `https://www.google.com/maps/search/?api=1&query=${RESORT_COORDS}`;

const email = computed(() => (config.public.contactEmail as string) || "");
const phone = computed(() => (config.public.whatsappNumber as string) || "");

// Mobile drawer order — chat-first (WhatsApp, Telegram), then visual socials
// (Instagram, Facebook). Diverges from the desktop tagline order on purpose:
// on phones, guests reach for messaging apps before scrolling feeds.
const MOBILE_SOCIAL_ORDER = ["WhatsApp", "Telegram", "Instagram", "Facebook"];

// Brand-color backgrounds — same palette already used for QuoteWidget CTAs
// so the drawer stays visually consistent with the booking buttons.
const SOCIAL_BG: Record<string, string> = {
  WhatsApp: "bg-green-500 hover:bg-green-600",
  Telegram: "bg-sky-500 hover:bg-sky-600",
  Instagram:
    "bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 hover:brightness-110",
  Facebook: "bg-blue-600 hover:bg-blue-700",
};
const socialLinks = computed(() => {
  const byTitle = new Map(SOCIAL_LINKS.map((l) => [l.title, l]));
  return MOBILE_SOCIAL_ORDER.map((title) => byTitle.get(title)).filter(
    (l): l is (typeof SOCIAL_LINKS)[number] => Boolean(l && l.href),
  );
});
</script>
